import { BlobServiceClient } from '@azure/storage-blob';
import { createSlice, PayloadAction, Store } from '@reduxjs/toolkit';
import { AffectedComponent, Incident, IncidentDTO } from './model';
import { ComponentDowntime, Period } from './model/ComponentDowntime';
import { RootState } from './store';
import {
  getAffectedComponents,
  mapComponentName,
  YEAR,
  ComponentMappingEntry,
} from './helpers';

type ComponentLastIncidents = {
  values: boolean[];
  uptime: string;
};

type SwiftConfig = {
  backend: 'swift';
  endpoint: string;
};

type BlobConfig = {
  backend: 'azure';
  account_name: string;
  container_name: string;
};

type UnknownConfig = {
  backend: string;
};

type StorageData = SwiftConfig | BlobConfig | UnknownConfig;

export type EnvsJson = StorageData & {
  infra_name?: string;
  tracker_id_site: string;
  tracker_url: string;
  componentsMapping: ComponentMappingEntry[];
};

export type LastIncidents = { [key: string]: ComponentLastIncidents };

type State = {
  all: IncidentDTO[];
  last24h: LastIncidents;
  ongoingIncident: IncidentDTO;
  infraName: string;
  componentsMapping: ComponentMappingEntry[];
  loading: boolean;
};

const initialState: State = {
  all: [],
  last24h: {},
  ongoingIncident: new Incident().serialize(),
  infraName: '',
  componentsMapping: [],
  loading: true,
};

export async function getConfig(): Promise<EnvsJson> {
  const result = await fetch('envs.json');
  const data = await result.json();
  if (!data.componentsMapping) {
    data.componentsMapping = [];
  }
  return data;
}

async function getIncidentsFromSwift(storageData: SwiftConfig) {
  if (!storageData.endpoint) {
    throw new Error('endpoint are required');
  }
  const endpoint = storageData.endpoint;
  const incidents: string[] = await fetch(endpoint, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache, no-store, max-stale: 0',
    },
  })
    .then((v) => v.json())
    .then((v) => v.map((element: any) => element.name));

  return Promise.all(
    incidents.map(async (objectName) => {
      const incident = await fetch(`${endpoint}/${objectName}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, max-stale: 0',
        },
      }).then((v) => v.json());
      incident.id = objectName.replace('.json', '');
      return incident;
    })
  );
}

async function getIncidentsFromBlob(storageData: BlobConfig) {
  const incidents = [];
  if (!storageData.account_name || !storageData.container_name) {
    throw new Error('both account_name and container_name are required');
  }
  const account: string = storageData.account_name;
  const container: string = storageData.container_name;

  const blobServiceClient = new BlobServiceClient(
    `https://${account}.blob.core.windows.net/`
  );
  const containerClient = blobServiceClient.getContainerClient(container);
  for await (const blob of containerClient.listBlobsFlat()) {
    const response = await fetch(
      `https://${account}.blob.core.windows.net/${container}/${blob.name}`,
      {
        headers: {
          'Cache-Control': 'no-cache, no-store, max-stale: 0',
        },
      }
    );
    const data = await response.json();
    data.id = blob.name.replace('.json', '');
    incidents.push(data);
  }
  return incidents;
}

function isBlobConfig(config: StorageData): config is BlobConfig {
  return config.backend === 'azure';
}

function isSwiftConfig(config: StorageData): config is SwiftConfig {
  return config.backend === 'swift';
}

async function getIncidentsFromStorage(config: EnvsJson) {
  if (isBlobConfig(config)) {
    return getIncidentsFromBlob(config);
  }
  if (isSwiftConfig(config)) {
    return getIncidentsFromSwift(config);
  }
  throw new Error(`Unknown backend: ${config.backend}`);
}

export async function FetchIncidents(store: Store) {
  const config: EnvsJson = await getConfig();
  const incidents = await getIncidentsFromStorage(config);
  const sortedIncidents: Incident[] = incidents
    .map((inc: IncidentDTO) => ({
      ...inc,
      affected: (inc.affected ?? []).map((ac) => ({
        name: mapComponentName(ac.name, config.componentsMapping),
        severity: ac.severity,
      })),
    }))
    .map((inc) => new Incident(inc))
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  store.dispatch(clearState());
  store.dispatch(incidentsLoaded());
  store.dispatch(setInfraName(config.infra_name ?? ''));
  store.dispatch(setComponentsMapping(config.componentsMapping));
  sortedIncidents.forEach((inc) => {
    store.dispatch(addIncident(inc.serialize()));
  });
  store.dispatch(updateLast24h());
  store.dispatch(updateUptime());
}

export const incidentSlice = createSlice({
  name: 'incidents',
  initialState,
  reducers: {
    clearState: (state) => {
      return initialState;
    },
    incidentsLoaded: (state: State) => {
      return {
        ...state,
        loading: false,
      };
    },
    setInfraName: (state: State, action: PayloadAction<string>) => {
      return {
        ...state,
        infraName: action.payload,
      };
    },
    setComponentsMapping: (
      state: State,
      action: PayloadAction<ComponentMappingEntry[]>
    ) => {
      return {
        ...state,
        componentsMapping: action.payload,
      };
    },
    updateLast24h: (state) => {
      const newState: State = state;
      const incidents = newState.all;
      const currentDate = new Date();

      // get affected componenents
      const affectedComponenets = getAffectedComponents(incidents);

      affectedComponenets.forEach((affectedComponent) => {
        newState.last24h[affectedComponent] = {
          values: Array(24).fill(true),
          uptime: '100',
        };
      });

      // get incidents from last 24h
      incidents.forEach((inc) => {
        const newIncident = new Incident(inc);
        if (newIncident.presentXHoursAgo(24) || newIncident.type === 'ok') {
          for (
            let i = newIncident.getHoursSinceEnd(currentDate);
            i <= newIncident.getHoursSinceStart(currentDate) && i < 24;
            i++
          ) {
            newIncident.affected.forEach(function (ac: AffectedComponent) {
              const current = newState.last24h[ac.name];
              const lastComponents: boolean[] = current?.values ?? [];

              // Handle situation when component is marked as healthy - ok
              if (newIncident.type === 'ok' || ac.severity === 'ok') {
                lastComponents[lastComponents.length - i - 1] = true;
              } else {
                lastComponents[lastComponents.length - i - 1] = false;
              }
              if (current) {
                current.values = lastComponents;
              }
            });
          }
        }
      });
      return newState;
    },
    updateUptime: (state) => {
      const newState: State = state;
      const incidents = newState.all;
      const currentDate = new Date();
      const downtimeMinutes: { [key: string]: number } = {};
      const cy = currentDate.getUTCFullYear();
      const cm = currentDate.getUTCMonth();
      const minutesInMonth: number = new Date(cy, cm, 0).getDate() * 24 * 60;

      incidents.forEach((inc) => {
        const newIncident = new Incident(inc);
        // get uptime from current month
        if (!newIncident.isFinished() || newIncident.isSameMonth(new Date())) {
          newIncident.affected.forEach((affectedComponent) => {
            if (affectedComponent.name in downtimeMinutes) {
              downtimeMinutes[affectedComponent.name]! +=
                newIncident.getMinutesOfDowntimeAt(currentDate);
            } else {
              downtimeMinutes[affectedComponent.name] =
                newIncident.getMinutesOfDowntimeAt(currentDate);
            }
          });
          Object.keys(downtimeMinutes).forEach(async (component) => {
            if (!(component in state.last24h)) {
              newState.last24h[component] = {
                values: Array(24).fill(true),
                uptime: '100',
              };
            }
            const current = newState.last24h[component];
            const downtime = downtimeMinutes[component];
            if (current && downtime) {
              current.uptime = (
                ((minutesInMonth - downtime) / minutesInMonth) *
                100
              ).toFixed(2);
            }
          });
        }
      });
      return newState;
    },
    addIncident: (state, action: PayloadAction<IncidentDTO>) => {
      const newState: State = state;
      newState.all.unshift(action.payload);
      const newIncident = new Incident(action.payload);
      if (!newIncident.isFinished() && newIncident.type !== 'ok') {
        let current: Incident;
        if (newState.ongoingIncident.id) {
          current = new Incident(state.ongoingIncident);
          current.merge(newIncident);
          newState.ongoingIncident = current.serialize();
        } else {
          newState.ongoingIncident = newIncident.serialize();
        }
      }
      return newState;
    },
    reloadIncidents: (state, action: PayloadAction<State>) => {
      return action.payload;
    },
  },
});

export const {
  reloadIncidents,
  addIncident,
  updateLast24h,
  updateUptime,
  clearState,
  incidentsLoaded,
  setInfraName,
  setComponentsMapping,
} = incidentSlice.actions;
export const selectPastIncidents = (state: RootState) =>
  state.incidents.all
    .map((inc) => new Incident(inc))
    .filter((inc) => inc.type !== 'ok' && inc.isFinished());
export const selectLast24h = (state: RootState) => state.incidents.last24h;
export const selectOngoingIncident = (state: RootState) =>
  new Incident(state.incidents.ongoingIncident);

export const selectIsLoading = (state: RootState) => state.incidents.loading;

export const selectComponentDowntime = (state: RootState) =>
  state.incidents.all
    .map((inc) => new Incident(inc))
    .flatMap((inc) =>
      inc.affected.map(
        (ac) =>
          new ComponentDowntime(
            ac.name,
            ac.severity,
            new Period(
              inc.start,
              inc.end >= inc.start ? inc.end : new Date(Date.now() + YEAR)
            ),
            inc.details,
            inc.title,
            inc.end
          )
      )
    );

export const selectIncidentsByComponent = (state: RootState) =>
  state.incidents.all
    .map((inc) => new Incident(inc))
    .filter((inc) => inc.type !== 'ok' && inc.isFinished())
    .reduce((acc: Map<string, Incident[]>, inc) => {
      inc.affected.forEach((ac) => {
        if (ac.severity !== 'ok') {
          acc.set(ac.name, [...(acc.get(ac.name) ?? []), inc]);
        }
      });
      return acc;
    }, new Map());

export const selectFutureMaintenance = (state: RootState) =>
  state.incidents.all
    .map((inc) => new Incident(inc))
    .filter(
      (inc) => inc.end.getTime() > Date.now() && inc.type === 'maintenance'
    )
    .sort((a, b) => b.start.getTime() - a.start.getTime());

export const selectComponentsMapping = (state: RootState) =>
  state.incidents.componentsMapping;

export default incidentSlice.reducer;
